import { useContext, useState, useMemo } from "react";
import { toast } from "sonner";
import { RefreshCw, Eye, Loader2, ShieldCheck, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import MainSectionSection from "../../components/MainSectionSection";
import MerchantContext from "../../contexts/MerchantContext";
import { trpc } from "../../trpc";
import type { RouterOutputs } from "@/trpc";
import CreateTemplateModal from "../../components/CreateTemplateModal";
import TemplatePreviewModal from "../../components/TemplatePreviewModal";
import PromoteToSystemModal from "../../components/PromoteToSystemModal";

type Template = RouterOutputs["whatsappTemplates"]["list"]["templates"][number];

type TemplateCategory = "MARKETING" | "UTILITY" | "AUTHENTICATION";
type TemplateStatus = "PENDING" | "APPROVED" | "REJECTED" | "DELETED" | "FAILED";

// A template is a system template when it carries a `systemId` — the routing key on
// `gsi1` that automated flows resolve. The stored `isSystem` boolean can drift from it
// (e.g. a manual DynamoDB edit sets `systemId` without `isSystem`), so `systemId` is the
// source of truth everywhere in this view, never `isSystem`.
const isSystemTemplate = (template: Template): boolean => !!template.systemId;

// Only APPROVED, non-auth templates that aren't already system can be promoted; auth
// templates must never route through the automated system-send path.
const canPromote = (template: Template): boolean =>
  template.status === "APPROVED" &&
  template.category !== "AUTHENTICATION" &&
  !isSystemTemplate(template);

// Merchant-facing label for a system row: the role it serves, plus its scope prefix.
const getSystemLabel = (template: Template): string | null => {
  if (!isSystemTemplate(template)) return null;
  if (!template.role) return "Sistema";
  return template.role.scopePrefix
    ? `${template.role.label} · ${template.role.scopePrefix}`
    : template.role.label;
};

const getLanguageLabel = (code: string): string => {
  const languages: Record<string, string> = {
    es: "Espanol",
    en: "Ingles",
    pt: "Portugues",
  };
  return languages[code] || code;
};

const getCategoryLabel = (category: TemplateCategory) => {
  switch (category) {
    case "MARKETING":
      return "Marketing";
    case "UTILITY":
      return "Utilidad";
    case "AUTHENTICATION":
      return "Autenticacion";
    default:
      return category;
  }
};

const statusBadgeConfig: Record<string, { label: string; className: string }> = {
  PENDING: { label: "Pendiente", className: "bg-yellow-100 text-yellow-800" },
  APPROVED: { label: "Aprobada", className: "bg-green-100 text-green-800" },
  REJECTED: { label: "Rechazada", className: "bg-red-100 text-red-800" },
  DELETED: { label: "Eliminada", className: "bg-red-200 text-red-900" },
  FAILED: { label: "Fallida", className: "bg-red-100 text-red-800" },
};

const getDisplayReason = (template: {
  status: TemplateStatus;
  statusReason?: string;
}): string | undefined =>
  (template.status === "FAILED" || template.status === "REJECTED") && template.statusReason
    ? template.statusReason
    : undefined;

const getStatusBadge = (status: TemplateStatus) => {
  const config = statusBadgeConfig[status] || {
    label: status,
    className: "bg-gray-100 text-gray-800",
  };

  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs", config.className)}>{config.label}</span>
  );
};

const SystemBadge = ({ label }: { label: string }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-800">
    <ShieldCheck className="size-3" />
    {label}
  </span>
);

const WhatsappTemplatesContainer = () => {
  const { merchantId } = useContext(MerchantContext);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<any>(null);
  const [promoteTemplate, setPromoteTemplate] = useState<Template | null>(null);
  const [demoteTarget, setDemoteTarget] = useState<Template | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);

  const { data, isLoading, refetch } = trpc.whatsappTemplates.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const templates = data?.templates || [];
  const connections = data?.connections || [];

  const groupedData = useMemo(() => {
    const grouped: Record<
      string,
      {
        wabaId: string;
        phoneNumbers: string[];
        templates: typeof templates;
      }
    > = {};

    // Group connections by wabaId
    connections.forEach((connection) => {
      if (!connection.wabaId) return;
      if (!grouped[connection.wabaId]) {
        grouped[connection.wabaId] = {
          wabaId: connection.wabaId,
          phoneNumbers: [],
          templates: [],
        };
      }
      if (connection.phoneNumber) {
        grouped[connection.wabaId].phoneNumbers.push(connection.phoneNumber);
      }
    });

    // Add templates to their respective wabaId groups
    templates.forEach((template) => {
      if (template.wabaId && grouped[template.wabaId]) {
        grouped[template.wabaId].templates.push(template);
      }
    });

    return Object.values(grouped);
  }, [templates, connections]);

  const syncStatuses = trpc.whatsappTemplates.syncStatuses.useMutation({
    onSuccess: (data) => {
      refetch();
      if (data.errors.length > 0) {
        toast.warning("Sincronizacion completada", {
          description: data.message,
        });
        console.error("Sync errors:", data.errors);
      } else {
        toast.success("Sincronizacion completada", {
          description: data.message,
        });
      }
    },
    onError: (error) => {
      toast.error("Error al sincronizar", {
        description: error.message || "No se pudieron sincronizar las plantillas",
      });
    },
  });

  const demoteFromSystem = trpc.whatsappTemplates.demoteFromSystem.useMutation({
    onSuccess: (template) => {
      refetch();
      toast.success("Plantilla removida del sistema", {
        description: `«${template.name}» ya no es una plantilla del sistema.`,
      });
    },
    onError: (error) => {
      toast.error("No se pudo quitar del sistema", {
        description: error.message || "Intenta de nuevo",
      });
    },
  });

  const deleteTemplate = trpc.whatsappTemplates.deleteTemplate.useMutation({
    onSuccess: (template) => {
      refetch();
      toast.success("Plantilla eliminada", {
        description: `«${template.name}» se eliminó de Palomma.`,
      });
    },
    onError: (error) => {
      toast.error("No se pudo eliminar", {
        description: error.message || "Intenta de nuevo",
      });
    },
  });

  const handleCreateTemplate = () => {
    setIsCreateModalOpen(true);
  };

  const handleConfirmDemote = () => {
    if (!demoteTarget) return;
    demoteFromSystem.mutate({ merchantId, id: demoteTarget.id });
    setDemoteTarget(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteTemplate.mutate({ merchantId, id: deleteTarget.id });
    setDeleteTarget(null);
  };

  const handleSyncStatuses = () => {
    syncStatuses.mutate({ merchantId });
  };

  const handlePreviewTemplate = (template: any) => {
    setPreviewTemplate(template);
  };

  return (
    <MainSectionSection>
      <CreateTemplateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => refetch()}
      />

      <TemplatePreviewModal
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        template={previewTemplate}
      />

      <PromoteToSystemModal
        isOpen={!!promoteTemplate}
        onClose={() => setPromoteTemplate(null)}
        onSuccess={() => refetch()}
        template={promoteTemplate}
        onRequestDemote={() => {
          const target = promoteTemplate;
          setPromoteTemplate(null);
          setDemoteTarget(target);
        }}
      />

      <AlertDialog open={!!demoteTarget} onOpenChange={(open) => !open && setDemoteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitar del sistema</AlertDialogTitle>
            <AlertDialogDescription>
              {demoteTarget
                ? `«${demoteTarget.name}» dejará de usarse en los mensajes automáticos. La plantilla se conserva y podrás volver a usarla como plantilla del sistema.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-11">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDemote}
              className="min-h-11 bg-red-500 text-white hover:bg-red-600"
            >
              Quitar del sistema
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar plantilla</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `«${deleteTarget.name}» se eliminará de Palomma de forma permanente${
                    isSystemTemplate(deleteTarget)
                      ? " y los mensajes automáticos que la usan dejarán de funcionar."
                      : "."
                  }`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-11">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="min-h-11 bg-red-500 text-white hover:bg-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-0">
          <h2 className="text-xl font-bold md:text-2xl">Plantillas de WhatsApp</h2>
          <div className="flex flex-col gap-3 md:flex-row">
            <Button
              variant="outline"
              onClick={handleSyncStatuses}
              disabled={syncStatuses.isLoading}
              className="min-h-11 w-full border-green-500 text-green-600 hover:bg-green-500 hover:text-white md:w-auto"
            >
              {syncStatuses.isLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              Sincronizar estados
            </Button>
            <Button
              onClick={handleCreateTemplate}
              className="min-h-11 w-full bg-green-500 text-white hover:bg-green-600 md:w-auto"
            >
              Crear plantilla
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : groupedData.length > 0 ? (
        <div className="flex flex-col gap-6">
          {groupedData.map((group) => (
            <div key={group.wabaId} className="rounded-lg border bg-white p-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-0">
                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-500">WABA ID</p>
                    <p className="mb-2 break-all font-mono text-sm text-gray-700">{group.wabaId}</p>
                    {group.phoneNumbers.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-gray-600">
                          Numeros disponibles:
                        </span>
                        {group.phoneNumbers.map((phoneNumber, idx) => (
                          <span
                            key={idx}
                            className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800"
                          >
                            {phoneNumber}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                    {group.templates.length} plantilla{group.templates.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {group.templates.length > 0 && (
                  <>
                    <hr className="border-gray-200" />

                    {/* Mobile card view */}
                    <div className="md:hidden">
                      {group.templates.map((template, index) => (
                        <div
                          key={template.id}
                          className={cn(
                            "p-3",
                            index < group.templates.length - 1 && "border-b border-gray-200"
                          )}
                        >
                          <div className="mb-1 flex items-center justify-between">
                            <span className="mr-2 flex-1 truncate text-sm font-medium">
                              {template.name}
                            </span>
                            {getStatusBadge(template.status)}
                          </div>
                          {getSystemLabel(template) && (
                            <div className="mb-1">
                              <SystemBadge label={getSystemLabel(template)!} />
                            </div>
                          )}
                          {getDisplayReason(template) && (
                            <p className="mb-1 truncate text-xs text-red-600">
                              {getDisplayReason(template)}
                            </p>
                          )}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-600">
                                {getCategoryLabel(template.category)}
                              </span>
                              <span className="text-xs text-gray-400">·</span>
                              <span className="text-xs text-gray-600">
                                {getLanguageLabel(template.language)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePreviewTemplate(template)}
                                className="min-h-11"
                              >
                                <Eye className="size-4" />
                                Ver
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteTarget(template)}
                                className="min-h-11 text-red-600 hover:bg-red-50 hover:text-red-700"
                              >
                                <Trash2 className="size-4" />
                                Eliminar
                              </Button>
                            </div>
                          </div>
                          {(canPromote(template) || isSystemTemplate(template)) && (
                            <div className="mt-2 flex">
                              {canPromote(template) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setPromoteTemplate(template)}
                                  className="min-h-11 w-full border-purple-400 text-purple-700 hover:bg-purple-50"
                                >
                                  <ShieldCheck className="size-4" />
                                  Usar como plantilla del sistema
                                </Button>
                              )}
                              {isSystemTemplate(template) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setPromoteTemplate(template)}
                                  className="min-h-11 w-full border-purple-400 text-purple-700 hover:bg-purple-50"
                                >
                                  <Pencil className="size-4" />
                                  Editar plantilla del sistema
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Desktop table view */}
                    <div className="hidden overflow-x-auto md:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              Estado
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              Nombre
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              Categoria
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              Idioma
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              Sistema
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-gray-500">
                              Acciones
                            </th>
                            <th className="px-3 py-2" />
                          </tr>
                        </thead>
                        <tbody>
                          {group.templates.map((template) => (
                            <tr key={template.id} className="border-b border-gray-100">
                              <td className="px-3 py-2">{getStatusBadge(template.status)}</td>
                              <td className="px-3 py-2 font-medium">
                                {template.name}
                                {getDisplayReason(template) && (
                                  <p className="max-w-[240px] truncate text-xs font-normal text-red-600">
                                    {getDisplayReason(template)}
                                  </p>
                                )}
                              </td>
                              <td className="px-3 py-2">{getCategoryLabel(template.category)}</td>
                              <td className="px-3 py-2">{getLanguageLabel(template.language)}</td>
                              <td className="px-3 py-2">
                                {getSystemLabel(template) ? (
                                  <SystemBadge label={getSystemLabel(template)!} />
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-end gap-1">
                                  {canPromote(template) && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setPromoteTemplate(template)}
                                      className="text-purple-700 hover:bg-purple-50"
                                    >
                                      <ShieldCheck className="size-4" />
                                      Sistema
                                    </Button>
                                  )}
                                  {isSystemTemplate(template) && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setPromoteTemplate(template)}
                                      className="text-purple-700 hover:bg-purple-50"
                                    >
                                      <Pencil className="size-4" />
                                      Editar
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handlePreviewTemplate(template)}
                                  >
                                    <Eye className="size-4" />
                                    Ver
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setDeleteTarget(template)}
                                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                  >
                                    <Trash2 className="size-4" />
                                    Eliminar
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="mb-4 text-lg text-gray-500">No hay plantillas de WhatsApp</p>
            <p className="mb-6 text-sm text-gray-400">
              Crea tu primera plantilla para empezar a enviar mensajes
            </p>
            <Button
              onClick={handleCreateTemplate}
              className="bg-green-500 text-white hover:bg-green-600"
            >
              Crear plantilla
            </Button>
          </div>
        </div>
      )}
    </MainSectionSection>
  );
};

export default WhatsappTemplatesContainer;
